import * as Sentry from '@sentry/react';
import { appInsights } from './appInsights';

type EventProperties = Record<string, any>;
type MetricAttributes = Record<string, string | number | boolean>;

type SentryMetricsApi = {
  count: (name: string, value?: number, options?: { attributes?: MetricAttributes; unit?: string }) => void;
  distribution: (name: string, value: number, options?: { attributes?: MetricAttributes; unit?: string }) => void;
};

const sentryWithMetrics = Sentry as typeof Sentry & { metrics?: SentryMetricsApi };

const parseMirroredEvents = (): Set<string> => {
  const raw = import.meta.env.VITE_SENTRY_MIRROR_CUSTOM_EVENTS;
  if (!raw) {
    return new Set<string>();
  }

  return new Set(
    raw
      .split(',')
      .map((name: string) => name.trim())
      .filter((name: string) => name.length > 0)
  );
};

const mirroredEventNames = parseMirroredEvents();
const mirrorAllEvents = import.meta.env.VITE_SENTRY_MIRROR_ALL_CUSTOM_EVENTS === 'true';

const shouldMirrorToSentry = (eventName: string): boolean => {
  if (!eventName) {
    return false;
  }

  return mirrorAllEvents || mirroredEventNames.has(eventName);
};

const toMetricAttributes = (eventName: string, properties?: EventProperties): MetricAttributes => {
  const attrs: MetricAttributes = {
    event_name: eventName
  };

  const source = properties?.source;
  if (typeof source === 'string' && source.length > 0) {
    attrs.source = source;
  }

  const isMandatory = properties?.isMandatory;
  if (typeof isMandatory === 'string' || typeof isMandatory === 'boolean') {
    attrs.is_mandatory = isMandatory;
  }

  return attrs;
};

const emitClientMetrics = (eventName: string, properties?: EventProperties): void => {
  try {
    const metrics = sentryWithMetrics.metrics;
    if (!metrics) {
      return;
    }

    const attributes = toMetricAttributes(eventName, properties);
    metrics.count('oms.custom_event.count', 1, { attributes });

    switch (eventName) {
      case 'AuthenticationError_401_Received':
        metrics.count('oms.auth.401.count', 1, { attributes });
        break;
      case 'AuthenticationError_TokenRefreshSucceeded':
        metrics.count('oms.auth.refresh.success.count', 1, { attributes });
        break;
      case 'AuthenticationError_TokenRefreshFailed':
        metrics.count('oms.auth.refresh.failure.count', 1, { attributes });
        break;
      case 'AuthenticationError_RetrySucceeded':
        metrics.count('oms.auth.retry.success.count', 1, { attributes });
        break;
      case 'AuthenticationError_InteractiveLoginFailed':
        metrics.count('oms.auth.interactive_login.failure.count', 1, { attributes });
        break;
      case 'VersionControl_UpdateReceived':
        metrics.count('oms.version.update.received.count', 1, { attributes });
        break;
      case 'VersionControl_UserClickedReload':
        metrics.count('oms.version.reload.user_click.count', 1, { attributes });
        break;
      case 'VersionControl_AutoReloadTriggered':
        metrics.count('oms.version.reload.auto.count', 1, { attributes });
        break;
      case 'VersionControl_UserDismissedUpdate':
        metrics.count('oms.version.update.dismissed.count', 1, { attributes });
        break;
      case 'OrderValidation_ClientSideFailed':
        metrics.count('oms.order.validation.failure.count', 1, { attributes });
        break;
      default:
        break;
    }
  } catch (error) {
    console.warn('Telemetry metric emission failed', error);
  }
};

export const trackCustomEvent = (name: string, properties?: EventProperties): void => {
  try {
    appInsights.trackEvent({ name, properties });
  } catch (error) {
    console.warn('App Insights custom event tracking failed', error);
  }

  emitClientMetrics(name, properties);

  if (!shouldMirrorToSentry(name)) {
    return;
  }

  // Route mirrored custom events to the Sentry Logs pipeline (Logs tab) via Sentry.logger,
  // NOT to Issues via Sentry.captureMessage.
  try {
    const sentryLogger = (Sentry as any).logger;
    if (sentryLogger?.info) {
      // Sentry.logger only accepts scalar attribute values (string | number | boolean).
      const attrs: Record<string, string | number | boolean> = {
        customEventName: name,
        telemetrySource: 'appinsights-dualwrite',
      };
      if (properties) {
        Object.entries(properties).forEach(([key, value]) => {
          if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            attrs[key] = value;
          }
        });
      }
      sentryLogger.info(`custom_event:${name}`, attrs);
    }
  } catch (error) {
    console.warn('Sentry custom event logging failed', error);
  }
};
